import {locationMessages} from '@/messages'
import {jsonRpc, mediator, PartyHasAlreadyJoinedError} from '@communication'
import useRepository from './useRepository'
import {useAppStore, useUserStore, useSessionStore} from '@/stores'
import {useAsyncState} from '@vueuse/core'
import Session from '@communication/Session'
import {computed, ref} from 'vue'
import getURLParameter from '@/utils/getURLParameter'
import matchURLParameter from '@/utils/matchURLParameter'

// After a network change, also when followed by a reload, the server keeps this party on its old, silent socket for
// 15-20 s and answers con:join with PARTY_HAS_ALREADY_JOINED meanwhile. Retried like JsonRpc's re-join, about 30 s.
// A signaling socket that is not open yet (slow network, or reconnecting) is waited for within the same attempts.
// Review M50: on master in JoinView (bbc3af0); lost on dev when the pre-call screen replaced JoinView
const JOIN_RETRY_DELAY = 3000
const JOIN_RETRY_ATTEMPTS = 10
// The join was given up because the pre-call screen was left or another session was entered meanwhile
export class JoinAbortedError extends Error {}

export const usePreCallAction = () => {
  const app = useAppStore() as any
  const user = useUserStore() as any
  const session = useSessionStore() as any
  const {sendWsEvent} = useRepository()
  const joinMode = ref<'pin' | 'key'>('pin')
  const pinParam = getURLParameter('pin') || ''
  const pin = ref(pinParam)
  const key = getURLParameter('k') || ''
  const conference = getURLParameter('conference') || matchURLParameter(/^(\d{3}-){3}\d{3}$/) || ''
  if (conference || key) {
    session.conference = conference
    session.key = key
    joinMode.value = 'key'
  }
  const validInput = computed(() => {
    if (joinMode.value === 'pin') {
      return /^\d{6}$/.test(pin.value)
    }
    const format = /^(\d{3}-){3}\d{3}$/
    return !!(session?.conference?.match(format) && session.key.length > 0)
  })

  const {
    state: isSessionValid,
    execute: validateSession,
    error: sessionValidationError,
    isLoading: validatingSession,
  } = useAsyncState(
    async () => {
      const {
        app: {type: appType},
        session: {api: sessionApiEndpoint, pinApi: pinApiEndpoint},
      } = app.configuration
      if (joinMode.value === 'pin') {
        const {sessionId, partyId} = await Session.resolvePin(pinApiEndpoint, pin.value)
        session.conference = sessionId
        session.key = partyId
      }
      const {key, conference: callId} = session
      const sessionInfo = await mediator.createSession(sessionApiEndpoint, appType, key, callId)
      session.setInfo(sessionInfo)

      if (session?.info?.allowStartMuted) {
        user.mute('audio', true)
      }
      return true
    },
    false,
    {
      immediate: false,
    }
  )
  // PARTY_HAS_ALREADY_JOINED and a socket that is not open (or closes during the request) are retried; any other error
  // ends the join at once. A failed attempt publishes nothing on the server, it only re-sends the call state to the
  // parties already in the call
  const joinWithRetry = async () => {
    const joiningSession = mediator.session
    if (!joiningSession) throw new Error('Session not properly initialized')
    for (let attempt = 1; ; attempt++) {
      // con:join on a socket that is still opening fails at once, on a closed one it waits for the 60 s request timeout
      if (jsonRpc.isConnected) {
        try {
          return await joiningSession.join()
        } catch (error: any) {
          const retryable = error instanceof PartyHasAlreadyJoinedError || error?.message === 'WebSocket closed'
          if (!retryable || attempt >= JOIN_RETRY_ATTEMPTS) throw error
        }
      } else if (attempt >= JOIN_RETRY_ATTEMPTS) {
        throw new Error('Signaling not connected')
      }
      await new Promise(resolve => setTimeout(resolve, JOIN_RETRY_DELAY))
      if (!app.isPreCall || mediator.session !== joiningSession) throw new JoinAbortedError('Join abandoned')
    }
  }

  const {
    execute: joinSession,
    error: joinError,
    isLoading: isJoining,
  } = useAsyncState(
    async () => {
      if (!(await validateSession())) {
        return false
      }

      if (!mediator.session || !mediator.session.user) {
        console.error('Session not properly initialized')
        return false
      }

      // validateSession clears the flag when it is done: keep the "Joining..." state until the join itself ends
      const {user: userResponse, configuration: configurationResponse} = await joinWithRetry()
      user.setUser(userResponse.id, userResponse.email, userResponse.fullname, userResponse.party)
      session.setConfiguration(configurationResponse)
      app.goToCall()

      if (typeof user.location?.latitude === 'number' && typeof user.location?.longitude === 'number') {
        sendWsEvent(
          locationMessages.locationSet(
            user.location,
            session.info.account,
            session.info.team ?? '',
            session.info.license
          ) as any
        )
      }

      const expectedUrlParams = `?${session.conference}&k=${session.key}`
      if (window.location.search !== expectedUrlParams) {
        window.history.pushState({}, '', `${window.location.pathname}${expectedUrlParams}`)
      }
    },
    false,
    {immediate: false}
  )
  return {
    sessionValidationError,
    pin,
    joinMode,
    joinError,
    validatingSession,
    isJoining,
    validInput,
    joinSession,
    validateSession,
  }
}
